import { useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener');
  };

  return (
    <>
      <nav className={`w-full fixed top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 cursor-pointer z-50">
            <div className="w-9 h-9 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
              <i className="ri-grid-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <span className={`text-sm font-heading font-semibold transition-colors ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
              Superbuy Spreadsheet
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>
              Home
            </a>
            <a href="/what-is-superbuy-spreadsheet/" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>
              Beginner Guide
            </a>
            <a href="/is-superbuy-spreadsheet-safe/" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>
              Safety
            </a>
            <a href="/superbuy-spreadsheet-review/" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>
              Review
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 cursor-pointer"
            >
              <i className="ri-shopping-bag-line w-3.5 h-3.5 flex items-center justify-center"></i>
              Shop Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden z-50 w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-4">
              <span className={`absolute left-0 w-full h-0.5 rounded-full transition-all duration-300 ${mobileOpen ? 'top-2 rotate-45 bg-foreground-900' : `${scrolled ? 'bg-foreground-900' : 'bg-white'} top-0`}`}></span>
              <span className={`absolute left-0 w-full h-0.5 rounded-full transition-all duration-300 ${mobileOpen ? 'opacity-0' : `${scrolled ? 'bg-foreground-900' : 'bg-white'} top-2`}`}></span>
              <span className={`absolute left-0 w-full h-0.5 rounded-full transition-all duration-300 ${mobileOpen ? 'top-2 -rotate-45 bg-foreground-900' : `${scrolled ? 'bg-foreground-900' : 'bg-white'} top-4`}`}></span>
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="h-full flex flex-col pt-20 overflow-y-auto">
            {/* Quick Nav Links */}
            <div className="px-5 pb-4 border-b border-background-200/70">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Navigation</p>
              <div className="flex flex-col gap-1">
                <a href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-home-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Home</span>
                </a>
                <a href="/what-is-superbuy-spreadsheet/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-book-open-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Beginner Guide</span>
                </a>
                <a href="/how-to-use-superbuy-spreadsheet/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-guide-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">How to Use</span>
                </a>
                <a href="/is-superbuy-spreadsheet-safe/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-shield-check-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Safety Guide</span>
                </a>
                <a href="/superbuy-spreadsheet-qc/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-image-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">QC Guide</span>
                </a>
                <a href="/superbuy-spreadsheet-shipping-guide/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-ship-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Shipping Guide</span>
                </a>
                <a href="/superbuy-spreadsheet-review/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-star-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Review</span>
                </a>
                <a href="/superbuy-spreadsheet-alternative/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                  <i className="ri-scales-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-medium text-foreground-800">Alternatives</span>
                </a>
              </div>
            </div>

            {/* Categories */}
            <div className="px-5 py-4 border-b border-background-200/70">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Shop by Category</p>
              <div className="flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer w-full text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-accent-600 w-4 h-4 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 flex-1">{cat.name}</span>
                    <i className="ri-external-link-line text-foreground-300 w-4 h-4 flex items-center justify-center"></i>
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="px-5 py-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 cursor-pointer text-sm"
              >
                <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center"></i>
                Shop at kaspreadsheet.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}